import { FC } from "react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";

import {
  availableStatesAbbreviations,
  fetchAutocompletePlaceData,
  IAddressAutocompleteFields,
} from "@/components/common/addressInput/common";
import { clsx } from "clsx";
import { TYPED_ENV } from "@/utils/env";
import { clsxm } from "@/utils/clsxm";

const MAPS_API_KEY =
  TYPED_ENV.VITE_MAPS_API_KEY ?? "AIzaSyBUuMk9ahQ6Wl0-KcaMvMur21lIqTjGEq0";

export type InputProps = {
  addressValue: string;
  placeholder?: string;
  errorMessages?: boolean | string | string[];
  isNewDesign?: boolean;
  onChange: (
    addressValue: string,
    addressParsedValue: IAddressAutocompleteFields
  ) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
};

export const AddressAutocompleteInput: FC<InputProps> = ({
  addressValue,
  onChange,
  placeholder = "",
  errorMessages: errorMessagesProp,
  onBlur,
  isNewDesign,
}) => {
  const errorMessages = Array.isArray(errorMessagesProp)
    ? errorMessagesProp
    : [errorMessagesProp].filter(Boolean);

  const handleAddressInput = (value: string, action: { action: string }) => {
    // trigger only on actual user input
    // and ignore on dropdown item click
    if (action.action === "input-change") onChange(value, { address: value });
  };

  const handleAddressSelect = async (selectedOption: any) => {
    const selectedAddress = selectedOption.value;
    const placeId = selectedAddress.place_id;
    const addressValue =
      selectedAddress.structured_formatting.main_text || selectedOption;

    const addressData = await fetchAutocompletePlaceData(placeId);

    onChange(addressValue, {
      ...addressData,
      address: addressValue,
    });
  };

  return (
    <div data-testid="address-input">
      <label
        className={clsx(
          "text-sm mb-1 text-left block",
          errorMessages.length && !isNewDesign && "text-red",
          isNewDesign &&
            "text-new-design-gray-500/50 font-light text-sm sm:text-base"
        )}
        data-testid="address-input-label"
      >
        {"Address line 1"}
      </label>
      <GooglePlacesAutocomplete
        apiKey={MAPS_API_KEY}
        autocompletionRequest={{
          componentRestrictions: {
            country: ["us"],
          },
        }}
        selectProps={{
          styles: {
            input: (provided) => ({
              ...provided,
              input: {
                opacity: "1 !important",
              },
            }),
          },
          filterOption: ({ data }) => {
            const state = data.value.terms?.at(-1)?.value || "";

            return !!availableStatesAbbreviations.includes(state);
          },
          components: { DropdownIndicator: null, SingleValue: () => null },
          value: !addressValue
            ? ""
            : ({ label: addressValue, value: addressValue } as any),
          placeholder: placeholder,
          noOptionsMessage: () => null,
          inputValue: addressValue,
          onInputChange: handleAddressInput,
          onChange: handleAddressSelect,
          onBlur,
          openMenuOnClick: false,
          classNames: {
            control: (props) =>
              clsxm(
                "p-1 sm:p-2 !text-base !sm:text-lg !border-[3px] w-full !border-soft !rounded-lg",
                props.isFocused &&
                  "!border-medium-green !outline-none !shadow-none",
                errorMessages.length && "border-red border-[1px]",
                isNewDesign &&
                  "!border-new-design-gray-600/[0.08] !border !outline-none !shadow-none !rounded-2xl p-[5px] sm:p-[5px] hover:!cursor-text hover:!border-new-design-purple-300",
                isNewDesign &&
                  props.isFocused &&
                  "!border-new-design-purple-300",
                isNewDesign &&
                  errorMessages.length &&
                  "!border-new-design-red-400 hover:!border-new-design-red-400"
              ),
            container: () => "text-left",
          },
        }}
        onLoadFailed={(error) =>
          console.error("Could not load Google API", error)
        }
        debounce={300}
        minLengthAutocomplete={2}
        apiOptions={{ language: "en", region: "us" }}
      />
      {errorMessages?.map((errorMessage, index) => (
        <span key={index} className="text-left mt-2	text-red text-sm">
          {errorMessage}
        </span>
      ))}
    </div>
  );
};
